import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutline,
  SearchOutlined,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import { useAuth } from "../../context/AuthContext";
import { inventoryService, productsService } from "../../services";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import { addTab } from "../../tabs/tabsSlice";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const nowDateString = () => new Date().toISOString().slice(0, 10);

const sanitizeIntegerInput = (value) => {
  const next = String(value ?? "").replace(/[^\d]/g, "");
  if (!next) return "";
  return String(Math.max(1, parseInt(next, 10)));
};

const buildPresentationOptions = (productDetail) => {
  const product = productDetail?.product || productDetail;
  const rows = toArray(productDetail?.presentations).filter((row) => row?.is_active);
  return rows.map((presentation) => ({
    product_presentation_id: presentation.id,
    product_description: product?.description || "Producto",
    product_code: product?.code || "",
    presentation_name: presentation?.product_format?.name || "Presentacion",
    qty_in_base: Number(presentation?.qty_in_base || 0),
  }));
};

function CreateOutboundNotePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;

  const canCreate = Boolean(
    isSuperuser || effectivePerms.includes("inventory_outbound_create")
  );
  const canViewExits = Boolean(
    isSuperuser || effectivePerms.includes("inventory_outbound_view")
  );

  const [reason, setReason] = useState("");
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [presentationOptions, setPresentationOptions] = useState([]);
  const [selectedProductLabel, setSelectedProductLabel] = useState("");
  const [loadingPresentations, setLoadingPresentations] = useState(false);
  const [stockByPresentation, setStockByPresentation] = useState({});

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  useEffect(() => {
    dispatch(
      addTab({
        path: "/inventory/outbound/create",
        label: "Nueva nota de salida",
        closable: true,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const query = searchTerm.trim();
    if (query.length < 2) {
      setSearchResults([]);
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await productsService.list({ search: query, active: "true" });
        if (!cancelled) setSearchResults(toArray(data));
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchTerm]);

  const fetchPresentationStock = useCallback(
    async (productPresentationId, qtyInBase) => {
      if (!branchId || !productPresentationId) return 0;
      try {
        const stockRow = await inventoryService.getStock({
          branch_id: branchId,
          product_presentation_id: productPresentationId,
        });
        const qtyBase = Number(stockRow?.qty_base || 0);
        const maxUnits = qtyInBase > 0 ? Math.max(0, Math.floor(qtyBase / qtyInBase)) : 0;
        setStockByPresentation((prev) => ({ ...prev, [String(productPresentationId)]: maxUnits }));
        return maxUnits;
      } catch {
        setStockByPresentation((prev) => ({ ...prev, [String(productPresentationId)]: 0 }));
        return 0;
      }
    },
    [branchId]
  );

  const loadProductPresentations = useCallback(
    async (product) => {
      if (!product?.id) return;
      setLoadingPresentations(true);
      try {
        const detail = await productsService.get(product.id);
        const options = buildPresentationOptions(detail);
        setPresentationOptions(options);
        setSelectedProductLabel(product.description || "Producto");
        await Promise.all(
          options.map((option) =>
            fetchPresentationStock(option.product_presentation_id, option.qty_in_base)
          )
        );
        if (options.length === 0) {
          showSnack("El producto no tiene presentaciones activas.", "warning");
        }
      } catch {
        setPresentationOptions([]);
        setSelectedProductLabel("");
        showSnack("No se pudieron cargar las presentaciones del producto.", "error");
      } finally {
        setLoadingPresentations(false);
      }
    },
    [fetchPresentationStock]
  );

  const addPresentationRow = async (option) => {
    if (!option?.product_presentation_id) return;
    const availableUnits =
      stockByPresentation[String(option.product_presentation_id)] ??
      (await fetchPresentationStock(option.product_presentation_id, option.qty_in_base));

    if (availableUnits <= 0) {
      showSnack("No hay stock disponible para esta presentacion.", "warning");
      return;
    }

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (row) => Number(row.product_presentation_id) === Number(option.product_presentation_id)
      );
      if (existingIndex >= 0) {
        return prev.map((row, index) => {
          if (index !== existingIndex) return row;
          const currentQty = Number(row.qty_presentation || 1);
          return {
            ...row,
            qty_presentation: String(Math.min(availableUnits, currentQty + 1)),
            stock_units: availableUnits,
          };
        });
      }
      return [
        ...prev,
        {
          id: `${option.product_presentation_id}-${Date.now()}`,
          product_presentation_id: option.product_presentation_id,
          product_description: option.product_description,
          presentation_name: option.presentation_name,
          qty_in_base: option.qty_in_base,
          qty_presentation: "1",
          stock_units: availableUnits,
        },
      ];
    });
  };

  const removeRow = (rowId) => {
    setItems((prev) => prev.filter((row) => row.id !== rowId));
  };

  const updateRowQty = (rowId, rawValue) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const sanitized = sanitizeIntegerInput(rawValue);
        if (!sanitized) return { ...row, qty_presentation: sanitized };
        const maxUnits = Number(row.stock_units || 0);
        const nextQty = maxUnits > 0 ? Math.min(Number(sanitized), maxUnits) : Number(sanitized);
        return { ...row, qty_presentation: String(nextQty) };
      })
    );
  };

  const normalizedItems = useMemo(
    () =>
      items
        .map((row) => ({
          product_presentation_id: Number(row.product_presentation_id),
          qty_presentation: Number(row.qty_presentation),
          stock_units: Number(row.stock_units || 0),
          product_description: row.product_description,
        }))
        .filter((row) => Number.isInteger(row.product_presentation_id) && row.product_presentation_id > 0)
        .filter((row) => Number.isInteger(row.qty_presentation) && row.qty_presentation > 0),
    [items]
  );

  const handleSave = async () => {
    if (submittingRef.current) return;

    if (!canCreate) {
      showSnack("No autorizado para crear notas de salida.", "warning");
      return;
    }
    if (!branchId) {
      showSnack("No hay sucursal activa.", "warning");
      return;
    }
    if (normalizedItems.length === 0) {
      showSnack("Agrega al menos un item valido.", "warning");
      return;
    }

    const invalidRow = normalizedItems.find(
      (row) => row.qty_presentation > row.stock_units && row.stock_units >= 0
    );
    if (invalidRow) {
      showSnack(
        `La cantidad de ${invalidRow.product_description || "un producto"} supera la existencia disponible.`,
        "warning"
      );
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const savedNote = await inventoryService.createOutboundNote({
        branch_id: branchId,
        reason: reason.trim(),
        items: normalizedItems.map(({ product_presentation_id, qty_presentation }) => ({
          product_presentation_id,
          qty_presentation,
        })),
      });
      if (!savedNote?.id) {
        throw new Error("La nota de salida no devolvio un id valido.");
      }
      showSnack("Nota de salida creada correctamente.", "success");
      if (canViewExits) {
        navigate("/inventory/outbound-notes");
      } else {
        setReason("");
        setItems([]);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo crear la nota de salida.";
      showSnack(detail, "error");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para crear notas de salida.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
          Nueva nota de salida
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => (canViewExits ? navigate("/inventory/outbound-notes") : navigate(-1))}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar"}
          </Button>
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert sx={{ mb: 2 }} />

      <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "350px 1fr", gap: 2 }}>
        <Card sx={{ borderLeft: `4px solid ${theme.palette.primary.main}` }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.25 }}>
              Datos generales
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Motivo"
                size="small"
                multiline
                minRows={4}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                fullWidth
              />
              <TextField label="Sucursal" size="small" value={currentBranch?.name || "-"} fullWidth disabled />
              <TextField
                label="Fecha"
                size="small"
                type="date"
                value={nowDateString()}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: `4px solid ${theme.palette.error.main}`, minWidth: 0 }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
              Items
            </Typography>

            <TextField
              label="Buscar producto"
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText="Busca por nombre o codigo"
              fullWidth
            />

            {searchTerm.trim().length >= 2 && (
              <Paper variant="outlined" sx={{ maxHeight: 170, overflowY: "auto", p: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Buscando...
                  </Typography>
                ) : searchResults.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin resultados.
                  </Typography>
                ) : (
                  <Stack spacing={0.7}>
                    {searchResults.map((row) => (
                      <Stack
                        key={row.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ p: 0.8, borderRadius: 1, bgcolor: "background.default" }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {row.description || "Producto"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {row.code || "-"}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddOutlined fontSize="small" />}
                          onClick={() => loadProductPresentations(row)}
                        >
                          Elegir
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 1, maxHeight: 190, overflowY: "auto" }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.8 }}>
                {selectedProductLabel ? `Presentaciones: ${selectedProductLabel}` : "Presentaciones"}
              </Typography>
              {loadingPresentations ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando presentaciones...
                </Typography>
              ) : presentationOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Selecciona un producto para ver sus presentaciones.
                </Typography>
              ) : (
                <Stack spacing={0.65}>
                  {presentationOptions.map((option) => {
                    const availableUnits = Number(
                      stockByPresentation[String(option.product_presentation_id)] || 0
                    );
                    return (
                      <Stack
                        key={option.product_presentation_id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ p: 0.75, borderRadius: 1, bgcolor: "background.default" }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {option.presentation_name}
                          </Typography>
                          <Stack direction="row" spacing={0.7} alignItems="center">
                            <Typography variant="caption" color="text.secondary" noWrap>
                              Qty base: {Number(option.qty_in_base || 0).toFixed(2)}
                            </Typography>
                            <Chip
                              size="small"
                              color={availableUnits > 0 ? "success" : "error"}
                              label={`Stock ${availableUnits}`}
                            />
                          </Stack>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddOutlined fontSize="small" />}
                          onClick={() => addPresentationRow(option)}
                          disabled={availableUnits <= 0}
                        >
                          Agregar
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ minHeight: 240, maxHeight: "calc(100vh - 430px)", overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Presentacion</TableCell>
                    <TableCell align="center">Stock</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Eliminar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          Agrega presentaciones para construir la nota.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.product_description || "-"}</TableCell>
                        <TableCell>{row.presentation_name || "-"}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            color={Number(row.stock_units || 0) > 0 ? "success" : "error"}
                            label={row.stock_units || 0}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={row.qty_presentation}
                            onChange={(event) => updateRowQty(row.id, event.target.value)}
                            inputProps={{ min: 1, step: "1", max: Number(row.stock_units || 0) || undefined }}
                            sx={{ width: 110 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => removeRow(row.id)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Divider />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="contained" onClick={handleSave} disabled={submitting}>
                {submitting ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default CreateOutboundNotePage;
